import { apiUrl } from "@/lib/apiUrl";
import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, splitLink, httpLink, TRPCClientError, TRPCLink } from "@trpc/client";
import { createRoot } from "react-dom/client";
import { observable } from "@trpc/server/observable";
import superjson from "superjson";
import App from "./App";
import { UIProvider } from "./contexts/UIContext";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // NÃO RETRY em AbortError
        if (error instanceof Error && error.name === 'AbortError') {
          console.log(`[QueryClient] AbortError detectado - NÃO retry`);
          return false;
        }
        if (error instanceof TRPCClientError && error.message.includes('aborted')) {
          console.log(`[QueryClient] TRPCClientError com 'aborted' - NÃO retry`);
          return false;
        }
        // Retry padrão para outros erros (máximo 3 tentativas)
        return failureCount < 3;
      },
    },
    mutations: {
      retry: (failureCount, error) => {
        // NÃO RETRY em AbortError
        if (error instanceof Error && error.name === 'AbortError') {
          console.log(`[QueryClient] AbortError detectado - NÃO retry`);
          return false;
        }
        if (error instanceof TRPCClientError && error.message.includes('aborted')) {
          console.log(`[QueryClient] TRPCClientError com 'aborted' - NÃO retry`);
          return false;
        }
        // Mutations não devem retry por padrão
        return false;
      },
    },
  },
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = "/login";
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    
    // NÃO logar AbortError (é fluxo normal de cancelamento)
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }
    if (error instanceof TRPCClientError && error.message.includes('aborted')) {
      return;
    }
    
    // Não logar erros operacionais esperados (validação/permissão) que já têm mensagens amigáveis para o usuário
    const trpcCode = (error as any)?.data?.code ?? (error as any)?.shape?.data?.code;
    const httpStatus = (error as any)?.data?.httpStatus ?? (error as any)?.shape?.data?.httpStatus;
    const isExpectedOperationalError = trpcCode === 'BAD_REQUEST' || trpcCode === 'FORBIDDEN' || trpcCode === 'TOO_MANY_REQUESTS' || httpStatus === 400 || httpStatus === 403 || httpStatus === 429;
    if (!isExpectedOperationalError) {
      console.error("[API Query Error]", error);
    }
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    
    // NÃO logar AbortError (é fluxo normal de cancelamento)
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }
    if (error instanceof TRPCClientError && error.message.includes('aborted')) {
      return;
    }
    
    // Não logar erros operacionais esperados (validação/permissão) que já têm mensagens amigáveis para o usuário
    const trpcCode = (error as any)?.data?.code ?? (error as any)?.shape?.data?.code;
    const httpStatus = (error as any)?.data?.httpStatus ?? (error as any)?.shape?.data?.httpStatus;
    const isExpectedOperationalError = trpcCode === 'BAD_REQUEST' || trpcCode === 'FORBIDDEN' || trpcCode === 'TOO_MANY_REQUESTS' || httpStatus === 400 || httpStatus === 403 || httpStatus === 429;
    if (!isExpectedOperationalError) {
      console.error("[API Mutation Error]", error);
    }
  }
});

// CORREÇÃO DEFINITIVA: Custom link que extrai signal do op.context
// Cada tentativa tem seu próprio AbortController e signal
// Não há global signal - signal vive apenas no escopo da tentativa
const customSignalLink: TRPCLink<any> = () => {
  return ({ op, next }) => {
    return observable((observer) => {
      const signal = (op.context as any)?.signal as AbortSignal | undefined;
      
      if (signal) {
        console.log(`[tRPC customSignalLink] ${op.path} - signal.aborted=${signal.aborted}`);
        
        // Se signal já está abortado, não prosseguir
        if (signal.aborted) {
          console.log(`[tRPC customSignalLink] ${op.path} - signal JÁ ABORTADO, não prosseguir`);
          observer.error(new Error('Request aborted before start'));
          return;
        }
        
        // Listener para abortar request se signal for abortado durante execução
        const abortHandler = () => {
          console.log(`[tRPC customSignalLink] ${op.path} - signal ABORTADO durante execução`);
          observer.error(new Error('Request aborted'));
        };
        
        signal.addEventListener('abort', abortHandler);
        
        // Cleanup listener após request completar
        const cleanup = () => {
          signal.removeEventListener('abort', abortHandler);
        };
        
        // Prosseguir com next link
        const subscription = next(op).subscribe({
          next: (value) => {
            observer.next(value);
          },
          error: (err) => {
            cleanup();
            observer.error(err);
          },
          complete: () => {
            cleanup();
            observer.complete();
          },
        });
        
        return () => {
          cleanup();
          subscription.unsubscribe();
        };
      } else {
        // Sem signal, prosseguir normalmente
        return next(op).subscribe(observer);
      }
    });
  };
};

// CORREÇÃO DEFINITIVA: Usar splitLink para isolar signal por tentativa
// ensureClassificationDraft usa httpLink (sem batching) para evitar contaminação de signal
// Outros endpoints usam httpBatchLink normalmente
const trpcClient = trpc.createClient({
  links: [
    customSignalLink,
    splitLink({
      // Condição: usar httpLink (sem batching) para ensureClassificationDraft
      condition: (op) => {
        const isEnsureDraft = op.path === 'ergos.classificationMatrix.ensureClassificationDraft';
        if (isEnsureDraft) {
          console.log(`[tRPC splitLink] ${op.path} -> httpLink (sem batching)`);
        }
        return isEnsureDraft;
      },
      // True: usar httpLink (sem batching) - signal isolado por request
      true: httpLink({
        url: apiUrl("/api/trpc"),
        transformer: superjson,
        fetch(url, options) {
          // Signal já foi extraído no customSignalLink
          // Aqui apenas fazemos o fetch com as options padrão
          return globalThis.fetch(url, {
            ...options,
            credentials: "include",
          });
        },
      }),
      // False: usar httpBatchLink para outros endpoints
      false: httpBatchLink({
        url: apiUrl("/api/trpc"),
        transformer: superjson,
        fetch(input, init) {
          return globalThis.fetch(input, {
            ...(init ?? {}),
            credentials: "include",
          });
        },
      }),
    }),
  ],
});

// ─── Handler global para erros fora do React ────────────────────────────────
// Captura erros que escapam dos Error Boundaries (ex: event handlers, async code)
// e reporta ao servidor silenciosamente. NUNCA exibe mensagem técnica.
window.onerror = (message, source, lineno, colno, error) => {
  // Ignorar erros inofensivos de DOM (removeChild race condition)
  const msg = String(message || '');
  if (msg.includes('removeChild') || msg.includes('NotFoundError') || msg.includes('insertBefore')) {
    return true; // Suprimir
  }
  // Reportar ao servidor
  try {
    fetch(apiUrl('/api/trpc/systemErrors.logPublicError'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        json: {
          errorType: 'FRONTEND',
          errorMessage: `[GLOBAL] ${msg}`.substring(0, 2000),
          errorStack: error?.stack?.substring(0, 10000) || `${source}:${lineno}:${colno}`,
          url: window.location.href.substring(0, 512),
          userAgent: navigator.userAgent.substring(0, 512),
          context: { source: 'window.onerror', timestamp: new Date().toISOString() },
        },
      }),
    }).catch(() => {});
  } catch {}
  return false;
};

window.onunhandledrejection = (event) => {
  const error = event.reason;
  const msg = error?.message || String(error || '');
  // Ignorar erros inofensivos
  if (msg.includes('removeChild') || msg.includes('NotFoundError') || msg.includes('aborted') || msg.includes('AbortError')) {
    return;
  }
  // Reportar ao servidor
  try {
    fetch(apiUrl('/api/trpc/systemErrors.logPublicError'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        json: {
          errorType: 'FRONTEND',
          errorMessage: `[UNHANDLED_REJECTION] ${msg}`.substring(0, 2000),
          errorStack: error?.stack?.substring(0, 10000),
          url: window.location.href.substring(0, 512),
          userAgent: navigator.userAgent.substring(0, 512),
          context: { source: 'window.onunhandledrejection', timestamp: new Date().toISOString() },
        },
      }),
    }).catch(() => {});
  } catch {}
};

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <UIProvider>
        <App />
      </UIProvider>
    </QueryClientProvider>
  </trpc.Provider>
);
